import { create, el, VNode, awaitRedraw } from "@dhx/ts-common/dom";
import { IHandlers } from "@dhx/ts-common/types";
import { EventSystem, IEventSystem } from "@dhx/ts-common/events";
import { getLabelStyle, locate } from "@dhx/ts-common/html";
import { View } from "@dhx/ts-common/view";
import { isEmptyObj } from "@dhx/ts-common/core";
import { DataCollection, DataEvents } from "@dhx/ts-data";
import { Uploader, UploaderEvents, IParams } from "@dhx/ts-vault";
import { Popup } from "@dhx/ts-popup";
import { baseProps, getFormItemCss, getValidationMessage, isVerify } from "../helper";
import locale from "../locales/en";
import {
	ValidationStatus,
	ISimpleVaultConfig,
	ISimpleVaultEventHandlersMap,
	ItemEvent,
	ISimpleVaultValue,
	ISimpleVault,
	ISimpleVaultProps,
} from "../types";

export class SimpleVault extends View implements ISimpleVault {
	public events: IEventSystem<UploaderEvents | ItemEvent | ISimpleVaultEventHandlersMap | any>;
	public data: DataCollection<ISimpleVaultValue>;

	protected _helper: Popup;
	private _uploader: Uploader;
	private _handlers: IHandlers;
	private _dragover: boolean;
	private _dragoverTimeout: any;
	private _isValid = true;
	private _propsItem = [
		"required",
		"label",
		"labelWidth",
		"labelPosition",
		"hiddenLabel",
		"helpMessage",
		"preMessage",
		"successMessage",
		"errorMessage",
	];
	private _propsSimpleVault = ["target", "singleRequest", "fieldName", "params"];
	private _props = [...baseProps, ...this._propsItem, ...this._propsSimpleVault];

	constructor(container: HTMLElement | string, config: ISimpleVaultConfig) {
		super(container, config);

		this.events = new EventSystem<DataEvents & UploaderEvents>(this);
		this.data = new DataCollection();
		this._uploader = new Uploader(
			{
				...(config as any),
				autosend: false,
				fieldName: config.fieldName || config.name || config.id,
			},
			this.data,
			this.data.events
		);

		this._initView(config);
		this._initHandlers();

		this._handlers = {
			add: (e: Event) => {
				if (this.config.disabled) {
					return;
				}
				e.preventDefault();
				this._uploader.selectFile();
			},
			remove: (e: Event) => {
				if (this.config.disabled) {
					return;
				}
				const id = locate(e);
				if (!id) {
					return;
				}
				this.data.remove(id);
			},
			ondragover: (e: DragEvent) => {
				const types = e.dataTransfer.types;
				for (const type of types) {
					if (type !== "Files" && type !== "application/x-moz-file") {
						return;
					}
				}
				if (this._dragoverTimeout) {
					clearTimeout(this._dragoverTimeout);
				} else {
					this.paint();
				}
				this._dragover = true;
				this._dragoverTimeout = setTimeout(() => {
					this._dragover = false;
					this._dragoverTimeout = null;
					this.paint();
				}, 150);
			},
			showHelper: e => {
				e.stopPropagation();
				e.preventDefault();
				this._helper.show(e.target, {
					mode: this.config.labelPosition === "left" ? "bottom" : "right",
				});
			},
			hideHelper: e => {
				e.preventDefault();
				e.stopPropagation();
				this._helper.hide();
			},
		};

		const render = () => this._draw();
		this.mount(container, create({ render }));
	}
	destructor() {
		this.events && this.events.clear();
		this._helper && this._helper.destructor();

		this.config = this.events = this._uid = this._helper = this._handlers = this._uploader = this.data = null;
		this._propsItem = this._propsSimpleVault = this._props = this._isValid = this._dragover = this._dragoverTimeout = null;
		this.unmount();
	}
	setProperties(propertyConfig: ISimpleVaultProps): void {
		if (
			!propertyConfig ||
			isEmptyObj(propertyConfig) ||
			!this.events.fire(ItemEvent.beforeChangeProperties, [this.getProperties()])
		)
			return;

		for (const key in propertyConfig) {
			if (this._props.includes(key)) {
				this.config[key] = propertyConfig[key];
				if (this._propsSimpleVault.includes(key)) {
					this._uploader.config[key] = propertyConfig[key];
				}
			}
		}
		this.events.fire(ItemEvent.afterChangeProperties, [this.getProperties()]);
		this.paint();
	}
	getProperties(): ISimpleVaultProps {
		const obj: ISimpleVaultProps = {};
		for (const prop of this._props) {
			obj[prop] = this.config[prop];
		}
		return obj;
	}
	show(): void {
		const { value, hidden } = this.config;
		if (!hidden || !this.events.fire(ItemEvent.beforeShow, [value])) {
			return;
		}
		this.config.hidden = false;
		this.events.fire(ItemEvent.afterShow, [value]);
	}
	hide(init?: boolean): void {
		const { value, hidden } = this.config;
		if ((hidden && !init) || !this.events.fire(ItemEvent.beforeHide, [value, init])) {
			return;
		}
		this.config.hidden = true;
		this.events.fire(ItemEvent.afterHide, [value, init]);
	}
	isVisible(): boolean {
		return !this.config.hidden;
	}
	disable(): void {
		this.config.disabled = true;
		this.paint();
	}
	enable(): void {
		this.config.disabled = false;
		this.paint();
	}
	isDisabled(): boolean {
		return this.config.disabled;
	}
	validate(silent = false): boolean {
		const { required, value } = this.config;

		if (!silent && !this.events.fire(ItemEvent.beforeValidate, [value])) return;
		this._isValid = !required || this.data.getLength() > 0;
		!silent && this.events.fire(ItemEvent.afterValidate, [value, this._isValid]);

		return this._isValid;
	}
	clearValidate(): void {
		this.config.$validationStatus = ValidationStatus.pre;
		this.paint();
	}
	clear(): void {
		if (this.getValue().length === 0) return;
		this.data.removeAll();
		this.paint();
	}
	getValue(): ISimpleVaultValue[] {
		return this.data.serialize();
	}
	selectFile(): void {
		this._uploader.selectFile();
	}
	send(params?: IParams): void {
		if (isVerify(this.config) && !this.validate(true)) {
			return;
		}
		this._uploader.send(params);
	}
	setValue(value: ISimpleVaultValue[]): void {
		if (!value.length) {
			return;
		}
		this.data.parse(value);
		isVerify(this.config) && this.validate();
	}
	protected _initView(config: ISimpleVaultConfig): void {
		if (isEmptyObj(config)) {
			throw new Error("Check the configuration is correct");
		}
		this.config = {
			type: config.type,
			id: config.id,
			name: config.name,
			fieldName: config.fieldName || config.name || config.id,
			disabled: false,
			hidden: false,
			singleRequest: false,
			target: "",
			value: [],
			label: "",
			labelWidth: "",
			labelPosition: "top",
			hiddenLabel: false,
			helpMessage: "",
			preMessage: "",
			successMessage: "",
			errorMessage: "",
			width: "content",
			height: "content",
		};
		for (const key in config) {
			if (key !== "id" && key !== "type" && key !== "name") {
				this.config[key] = config[key];
			}
		}
		if (this.config.hidden) {
			awaitRedraw().then(() => {
				this.hide(true);
			});
		}

		this.config.value.length && this.setValue(this.config.value);

		this.paint();
	}
	protected _initHandlers() {
		this.data.events.on(ItemEvent.change, (_id, method) => {
			const value = (this.config.value = this.getValue());
			this.events.fire(ItemEvent.change, [value]);
			method && isVerify(this.config) && this.validate();
			this.paint();
		});
		this.events.on(ItemEvent.afterValidate, () => {
			this.config.$validationStatus = this._isValid ? ValidationStatus.success : ValidationStatus.error;
			this.paint();
		});
		this._uploader.events.on("beforeUploadFile", file => {
			return this.events.fire(ItemEvent.beforeUploadFile, [file, this.config.value]);
		});
		this._uploader.events.on("uploadBegin", file => {
			this.events.fire(ItemEvent.uploadBegin, [file, this.config.value]);
		});
		this._uploader.events.on("uploadComplete", file => {
			this.events.fire(ItemEvent.uploadComplete, [file, this.config.value]);
		});
		this._uploader.events.on("uploadFail", file => {
			this.events.fire(ItemEvent.uploadFail, [file, this.config.value]);
		});
		this._uploader.events.on("uploadFile", (file, extra) => {
			this.events.fire(ItemEvent.uploadFile, [file, this.config.value, extra]);
		});
		this._uploader.events.on("uploadProgress", file => {
			this.events.fire(ItemEvent.uploadProgress, [file, this.config.value]);
		});
	}
	private _draw() {
		if (this.config.helpMessage) {
			if (this._helper) {
				this._helper.attachHTML(this.config.helpMessage);
			} else {
				this._helper = new Popup({
					css: "dhx_tooltip dhx_tooltip--forced dhx_tooltip--light",
				});
				this._helper.attachHTML(this.config.helpMessage);
			}
		}
		const files = this.data.getLength()
			? el(
					"ul.dhx_simplevault__files.dhx_simplevault-files",
					{
						class: this.config.$vaultHeight ? "" : "dhx_simplevault-files__fixed",
					},
					this.data.map(file =>
						el("li.dhx_simplevault-files__item", [
							el(
								"span.dhx_simplevault-files__item-name",
								(file.file && file.file.name) || file.name
							),
							el(
								".dhx_button.dhx_simplevault-files__delete.dhx_button--icon.dhx_button--view_link.dhx_button--size_small.dhx_button--color_secondary.dhx_button--circle",
								{
									"data-dhx-id": file.id,
									onclick: this._handlers.remove,
								},
								[el("span.dxi.dxi-delete-forever")]
							),
						])
					)
			  )
			: null;

		const { id, helpMessage, disabled, required, label } = this.config;
		const labelStyle = getLabelStyle(this.config);

		return el(
			".dhx_form-group.dhx_form-group--simplevault",
			{
				class: getFormItemCss(this.config, isVerify(this.config)),
			},
			[
				labelStyle &&
					el(
						"legend.dhx_label",
						{
							class: helpMessage ? "dhx_label--with-help" : "",
							style: labelStyle.style,
							onclick: this._handlers.add,
							"aria-label": label || "select files",
						},
						helpMessage
							? [
									(labelStyle.label || required) &&
										el("span.dhx_label__holder", labelStyle.label),
									el("span.dhx_label-help.dxi.dxi-help-circle-outline", {
										tabindex: "0",
										role: "button",
										onclick: this._handlers.showHelper,
										onfocus: this._handlers.showHelper,
										onblur: this._handlers.hideHelper,
									}),
							  ]
							: labelStyle.label
					),
				el(".dhx_input__wrapper", { id: this._uid }, [
					el(
						"div",
						{
							_hooks: {
								didInsert: (node: VNode) => {
									this._uploader.linkDropArea(node.el);
								},
							},
							ondragover: this._handlers.ondragover,
							class: "dhx_simplevault" + (this._dragover ? " dhx_simplevault--on-drag" : ""),
						},
						[
							el("div.dhx_simplevault-loader", [
								el("span.dhx_simplevault__icon.dxi.dxi-vault"),
							]),
							el(".dhx_simplevault__drop-area", [
								el("input.dhx_simplevault__input", {
									type: "file",
									id,
									disabled,
								}),
								el("span.dhx_simplevault__icon.dxi.dxi-vault"),
								el("span.dhx_simplevault__title", [
									el("span", locale.simpleVaultText),
									el("br"),
									el(
										"label.dhx_simplevault__label",
										{
											onclick: this._handlers.add,
											for: id,
										},
										" " + locale.simpleVaultLabel
									),
								]),
							]),
							files,
						]
					),
					getValidationMessage(this.config) &&
						el("span.dhx_input__caption", {}, getValidationMessage(this.config)),
				]),
			]
		);
	}
}
